/**
 * The whole "fluffy pastel" look lives here.
 *
 * Rules of thumb the components rely on:
 *  - nothing has a hard corner — the smallest radius in the system is 14px;
 *  - shadows are soft, coloured and low-contrast, never neutral grey;
 *  - the two answer sides are always mint (A) and peach (B), everywhere.
 */
export const theme = {
  colors: {
    // Backdrop
    sky: '#fdf2f8',
    skyDeep: '#f5e6ff',
    cream: '#fffaf6',

    // Surfaces
    surface: '#ffffff',
    surfaceSoft: '#fff6fb',
    surfaceSunken: '#f8eef7',

    // Ink
    ink: '#5b4a63',
    inkSoft: '#8d7d96',
    inkFaint: '#b8abc0',

    // Personality
    bubblegum: '#ffb3d1',
    bubblegumDeep: '#ff87b8',
    lavender: '#c9b6f7',
    lavenderDeep: '#a98ff0',
    mint: '#a8e6cf',
    mintDeep: '#6fd6ac',
    peach: '#ffcba4',
    peachDeep: '#ffab6e',
    lemon: '#ffe8a3',
    sky2: '#a8d8ff',

    // Feedback
    heart: '#ff7b9c',
    heartLost: '#e2d5e8',
    danger: '#ff6b8a',
    success: '#6fd6ac',
  },

  /**
   * Side A is always mint, side B is always peach — on the voting buttons, the
   * reveal columns and the tallies. Consistency beats variety: players learn
   * "I picked the mint one" and the reveal confirms it at a glance.
   */
  choice: {
    A: { soft: '#d4f5e6', base: '#a8e6cf', deep: '#4fbf95', ink: '#256b52' },
    B: { soft: '#ffe6d1', base: '#ffcba4', deep: '#f5954b', ink: '#8a4a1c' },
  },

  radii: {
    sm: '14px',
    md: '22px',
    lg: '32px',
    xl: '44px',
    pill: '999px',
  },

  shadows: {
    puff: '0 10px 30px rgba(197, 148, 189, 0.22)',
    puffLg: '0 18px 50px rgba(174, 130, 190, 0.26)',
    inset: 'inset 0 2px 6px rgba(160, 120, 170, 0.14)',
    /** The chunky bottom edge that makes buttons feel pressable. */
    lip: (color: string) => `0 6px 0 ${color}`,
  },

  fonts: {
    body: "'Baloo 2', 'Quicksand', 'Nunito', 'Segoe UI Rounded', 'Segoe UI', system-ui, sans-serif",
  },

  transitions: {
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  layout: {
    maxWidth: '860px',
  },
} as const;

export type AppTheme = typeof theme;
